import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DropzoneUpload } from 'models/Dropzone';
import { Field, Form } from 'react-final-form';
import DropzoneField from 'components/DropzoneField';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import validations from 'validations';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import {
  DOCUMENT_FILES,
  SINGLE_FILE_MAX_SIZE_MB,
  SINGLE_FILE_MAX_SIZE_DIRECT_UPLOAD,
} from 'data/global';

import './AddNdaModal.styles.scss';

type AddNdaModalProps = {
  className?: string;
} & ModalProps;

const AddNdaModal: React.ForwardRefRenderFunction<
  ModalRef,
  AddNdaModalProps
> = (props, ref) => {
  const { className, ...rest } = props;

  const { popSuccess, popServerError, popError } = useContext(
    PopNotificationsContext,
  );
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const getMixpanelProps = useGetMixpanelProps();

  const classes = classNames('add-nda-modal', className);

  const { t } = useTranslation();

  const uploadNDA: DropzoneUpload = useCallback(
    async (file, _, config) => {
      if (file.size > SINGLE_FILE_MAX_SIZE_DIRECT_UPLOAD) {
        popError(t('ndaTooLarge', { size: SINGLE_FILE_MAX_SIZE_MB }));
        return;
      }

      try {
        const { uploadedFile, temporaryFileId } =
          await dataRoomApi.nda.uploadNdaWithoutCreate(
            dataRoomId,
            file,
            config,
          );

        if (uploadedFile && temporaryFileId)
          return { ...uploadedFile, name: file.name, temporaryFileId };

        return null;
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
        return null;
      }
    },
    [dataRoomId, getMixpanelProps, popError, t],
  );

  const onSubmit = useCallback(
    async ({ dataRoomNda }) => {
      try {
        await dataRoomApi.nda.submitNdaUpload(
          dataRoomId,
          dataRoomNda?.temporaryFileId,
        );
        popSuccess(
          t('uploadNdaSuccessfully', {
            documentName: dataRoomNda?.name || 'File',
          }),
        );
        modal?.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [dataRoomId, modal, popServerError, popSuccess, t],
  );

  return (
    <Modal ref={modalRef} className={classes} title={t('uploadNda')} {...rest}>
      <Form
        onSubmit={onSubmit}
        render={({ submitting, invalid, handleSubmit }) => (
          <form className={classes} onSubmit={handleSubmit}>
            <p className="description">{t('uploadNdaDescription')}</p>

            <Field
              name="dataRoomNda"
              accept={DOCUMENT_FILES}
              component={DropzoneField}
              maxFiles={1}
              showApplicationAsIframe
              dropMessage={t('pdfOrWord')}
              actionText={t('chooseFileOrDrag')}
              uploadRequest={uploadNDA}
              validate={validations.required()}
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>

              <Button type="submit" disabled={submitting || invalid}>
                {t('submit')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(AddNdaModal);
